const controllers = new WeakMap();
const sectionSelector = '[data-fill-header]';
const cartLinesUpdateEvent = 'shopify:cart:lines-update';
const drawerOpenEvent = 'theme-drawer:open';
const drawerCloseEvent = 'theme-drawer:close';

function focusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => element.offsetParent !== null || element === document.activeElement);
}

class FillHeader {
  constructor(root) {
    this.root = root;
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.openTrigger = root.querySelector('[data-fill-header-open]');
    this.layer = root.querySelector('[data-fill-header-layer]');
    this.drawer = root.querySelector('[data-fill-header-drawer]');
    this.cartTrigger = root.querySelector('[data-fill-header-cart-trigger]');
    this.stickyHeader = root.querySelector('.fill-header-main--sticky');
    this.announcement = root.querySelector('.fill-announcement');
    this.lastFocused = null;
    this.lastScrollY = window.scrollY;
    this.scrollTicking = false;
    this.scrollThreshold = 100;
    this.scrollDelta = 6;

    this.bind();
    this.updateAnnouncementHeight();
    this.syncCartFromStorage();
    this.updateStickyState();
  }

  bind() {
    this.openTrigger?.addEventListener('click', this.openMobileMenu, { signal: this.signal });

    this.root.querySelectorAll('[data-fill-header-close]').forEach((button) => {
      button.addEventListener('click', this.closeMobileMenu, { signal: this.signal });
    });

    this.drawer?.addEventListener('click', this.handleDrawerClick, { signal: this.signal });
    document.addEventListener('keydown', this.handleKeydown, { signal: this.signal });
    document.addEventListener('click', this.handleOutsideDropdownClick, { signal: this.signal });
    document.addEventListener(cartLinesUpdateEvent, this.handleCartLinesUpdate, { signal: this.signal });
    document.addEventListener(drawerOpenEvent, this.handleCartDrawerStateChange, { signal: this.signal });
    document.addEventListener(drawerCloseEvent, this.handleCartDrawerStateChange, { signal: this.signal });

    ['cart:update', 'cart:change', 'cart:refresh', 'ajaxCart:updated', 'product:added'].forEach((eventName) => {
      document.addEventListener(eventName, this.refreshCartCount, { signal: this.signal });
    });

    window.addEventListener('pageshow', this.handlePageShow, { signal: this.signal });
    window.addEventListener('resize', this.handleResize, { signal: this.signal });
    window.addEventListener('scroll', this.handleScroll, { passive: true, signal: this.signal });
    this.cartTrigger?.addEventListener('click', this.openCart, { signal: this.signal });
  }

  destroy() {
    this.closeMobileMenu({ restoreFocus: false });
    this.stickyHeader?.classList.remove('fill-header--hidden', 'fill-header--visible');
    this.abortController.abort();
    controllers.delete(this.root);
  }

  openMobileMenu = () => {
    if (!this.layer || !this.drawer || !this.openTrigger) return;

    this.lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.layer.hidden = false;
    document.body.classList.add('fill-header-mobile-open');
    this.openTrigger.setAttribute('aria-expanded', 'true');
    this.showStickyHeader();

    requestAnimationFrame(() => {
      this.layer?.classList.add('is-open');
      this.drawer?.focus();
    });
  };

  closeMobileMenu = (options = { restoreFocus: true }) => {
    if (!this.layer || !this.openTrigger) return;

    this.layer.classList.remove('is-open');
    this.openTrigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('fill-header-mobile-open');

    window.setTimeout(() => {
      if (this.layer) this.layer.hidden = true;
    }, 220);

    if (options.restoreFocus !== false) {
      const returnTarget = this.lastFocused || this.openTrigger;
      returnTarget?.focus?.();
    }
  };

  handleDrawerClick = (event) => {
    const target = event.target;
    if (target instanceof HTMLAnchorElement) {
      this.closeMobileMenu({ restoreFocus: false });
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.root.querySelectorAll('[data-fill-header-dropdown][open]').forEach((details) => {
        details.removeAttribute('open');
      });

      if (this.layer && !this.layer.hidden) {
        event.preventDefault();
        this.closeMobileMenu();
      }
      return;
    }

    if (event.key !== 'Tab' || !this.layer || this.layer.hidden || !this.drawer) return;

    const focusables = focusableElements(this.drawer);
    if (!focusables.length) {
      event.preventDefault();
      this.drawer.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  handleOutsideDropdownClick = (event) => {
    this.root.querySelectorAll('[data-fill-header-dropdown][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  };

  handleResize = () => {
    this.updateAnnouncementHeight();
    this.updateStickyState();
  };

  updateAnnouncementHeight() {
    const height = this.announcement && !this.announcement.hidden ? this.announcement.offsetHeight : 0;
    this.root.style.setProperty('--fill-header-announcement-height', `${height}px`);
  }

  handleScroll = () => {
    if (!this.stickyHeader || this.scrollTicking) return;

    this.scrollTicking = true;
    requestAnimationFrame(() => {
      this.updateStickyState();
      this.scrollTicking = false;
    });
  };

  updateStickyState() {
    if (!this.stickyHeader) return;

    const currentScrollY = Math.max(window.scrollY, 0);
    const delta = currentScrollY - this.lastScrollY;

    if (this.shouldKeepStickyVisible(currentScrollY)) {
      this.showStickyHeader();
      this.lastScrollY = currentScrollY;
      return;
    }

    if (Math.abs(delta) < this.scrollDelta) return;

    if (delta > 0 && currentScrollY > this.scrollThreshold) {
      this.hideStickyHeader();
    } else if (delta < 0) {
      this.showStickyHeader();
    }

    this.lastScrollY = currentScrollY;
  }

  shouldKeepStickyVisible(scrollY) {
    if (scrollY <= this.scrollThreshold) return true;
    if (document.body.classList.contains('fill-header-mobile-open')) return true;
    if (this.root.querySelector('[data-fill-header-dropdown][open]')) return true;
    if (this.root.contains(document.activeElement)) return true;
    if (window.Shopify?.designMode) return true;
    return false;
  }

  showStickyHeader() {
    this.stickyHeader?.classList.remove('fill-header--hidden');
    this.stickyHeader?.classList.add('fill-header--visible');
  }

  hideStickyHeader() {
    this.stickyHeader?.classList.remove('fill-header--visible');
    this.stickyHeader?.classList.add('fill-header--hidden');
  }

  openCart = (event) => {
    const drawer = document.querySelector('theme-drawer#cart-drawer');

    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open();
      this.cartTrigger?.setAttribute('aria-expanded', 'true');
      return;
    }

    if (typeof window.Shopify?.actions?.openCart === 'function') {
      event.preventDefault();
      window.Shopify.actions.openCart();
      return;
    }

    window.location.href = window.Theme?.routes?.cart_url || '/cart';
  };

  handleCartLinesUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        if (typeof count === 'number') this.setCartCount(count);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[fill-header] Cart event rejected:', error);
      });
  };

  handleCartDrawerStateChange = (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    this.cartTrigger?.setAttribute('aria-expanded', event.type === drawerOpenEvent ? 'true' : 'false');
  };

  refreshCartCount = () => {
    const cartUrl = `${window.Theme?.routes?.cart_url || '/cart'}.js`;

    fetch(cartUrl, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (typeof cart?.item_count === 'number') this.setCartCount(cart.item_count);
      })
      .catch(() => {});
  };

  setCartCount(count) {
    this.root.querySelectorAll('[data-fill-header-cart-count]').forEach((element) => {
      element.textContent = String(count);
    });

    this.root.querySelectorAll('.fill-header-cart').forEach((element) => {
      const label = element.querySelector('.fill-header-cart-label')?.textContent?.trim() || 'Bag';
      element.setAttribute('aria-label', `${label}. ${count} items in cart`);
    });

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(count),
          timestamp: Date.now(),
        })
      );
    } catch (_) {}
  }

  syncCartFromStorage() {
    try {
      const stored = sessionStorage.getItem('cart-count');
      if (!stored) return;

      const { value, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp > 10000) return;

      const count = Number.parseInt(value, 10);
      if (Number.isFinite(count)) this.setCartCount(count);
    } catch (_) {}
  }

  handlePageShow = (event) => {
    if (event.persisted) this.refreshCartCount();
  };
}

function init(root = document) {
  root.querySelectorAll(sectionSelector).forEach((section) => {
    if (controllers.has(section)) return;
    controllers.set(section, new FillHeader(section));
  });
}

function unload(root) {
  root.querySelectorAll(sectionSelector).forEach((section) => {
    controllers.get(section)?.destroy();
  });
}

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:unload', (event) => unload(event.target));
